import { css } from "@emotion/css";
import {
  TagGroup as _TagGroup,
  TagGroupProps as AriaTagGroupProps,
  TagList,
  TagListProps,
  Key,
} from "react-aria-components";

import { _Tag } from "./assets/_Tag";

interface TagGroupProps<T>
  extends Omit<AriaTagGroupProps, "children">,
    Pick<TagListProps<T>, "items" | "children" | "renderEmptyState"> {
  label?: string;
  description?: string;
  errorMessage?: string;
  handleRemove: (key: Set<Key>) => void;
  tags: Record<string, any>[];
  testID: string;
  isDisabled?: boolean;
}

export function TagGroup<T extends object>(props: TagGroupProps<T>) {
  const {
    label,
    description,
    errorMessage,
    items,
    children,
    handleRemove,
    testID,
    isDisabled,
    ...rest
  } = props;
  const tagIds = props.tags?.map((tag) => tag?.id);

  return (
    <_TagGroup
      {...rest}
      disabledKeys={isDisabled ? tagIds : undefined}
      onRemove={(tag) => {
        // for some reason you can delete tags by tabbing back and forth
        // into a disabled tag to bypass the tab focus restriction
        if (!isDisabled) {
          handleRemove(tag);
        }
      }}
      aria-label={testID}
      data-testid={testID}
      className={styles.tagGroup}
    >
      <TagList className={styles.tagList} items={props.tags}>
        {(item) => (
          <_Tag isDisabled={isDisabled} id={item.id} textValue={item.value} />
        )}
      </TagList>
    </_TagGroup>
  );
}

const styles = {
  tagList: css({
    display: "flex",
    gap: "var(--space-2)",
    flexWrap: "wrap",
    padding: "0px",
  }),
  tagGroup: css({ padding: "0px !important" }),
};
